import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  TextField,
  Tooltip,
  Typography,
  Alert,
  Snackbar,
  CircularProgress,
} from '@mui/material';
import {
  DataGrid,
  GridColDef,
  GridRenderCellParams,
  GridToolbar,
} from '@mui/x-data-grid';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Download as DownloadIcon,
  CloudUpload as UploadIcon,
  ToggleOn as ToggleOnIcon,
  ToggleOff as ToggleOffIcon,
} from '@mui/icons-material';
import { Layout } from '../components/Layout';
import { adminApiService } from '../services/api';
import { AdminResource } from '../types';
import { useAuth } from '../auth/AuthProvider';
import { RESOURCES_WRITE_ROLES } from '../auth/roles';

// Format file size to human-readable string
const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
};

// Format date to readable string
const formatDate = (dateString: string): string => {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

interface ResourceDialogState {
  open: boolean;
  mode: 'add' | 'edit';
  resource: Partial<AdminResource> | null;
}

export const Resources: React.FC = () => {
  const { hasAnyRole } = useAuth();
  const canManageResources = hasAnyRole(RESOURCES_WRITE_ROLES);

  const [resources, setResources] = useState<AdminResource[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // Dialog state
  const [dialogState, setDialogState] = useState<ResourceDialogState>({
    open: false,
    mode: 'add',
    resource: null,
  });

  // Form state
  const [formName, setFormName] = useState('');
  const [formDescription, setFormDescription] = useState('');
  const [formDisplayOrder, setFormDisplayOrder] = useState(1);
  const [formFile, setFormFile] = useState<File | null>(null);
  const [formSubmitting, setFormSubmitting] = useState(false);

  // Track which resource is having its file replaced
  const [replacingFileId, setReplacingFileId] = useState<number | null>(null);

  const fetchResources = useCallback(async () => {
    setLoading(true);
    try {
      const response = await adminApiService.getResources(true);
      if (response.success && response.data) {
        setResources(response.data);
      } else {
        setError('Failed to load resources');
      }
    } catch (err) {
      console.error('Error fetching resources:', err);
      setError('An error occurred while fetching resources');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchResources();
  }, [fetchResources]);

  const handleOpenAddDialog = () => {
    setFormName('');
    setFormDescription('');
    setFormDisplayOrder(1);
    setFormFile(null);
    setDialogState({
      open: true,
      mode: 'add',
      resource: null,
    });
  };

  const handleOpenEditDialog = (resource: AdminResource) => {
    setFormName(resource.name);
    setFormDescription(resource.description || '');
    setFormDisplayOrder(resource.displayOrder);
    setFormFile(null);
    setDialogState({
      open: true,
      mode: 'edit',
      resource,
    });
  };

  const handleCloseDialog = () => {
    setDialogState({
      open: false,
      mode: 'add',
      resource: null,
    });
    setFormFile(null);
  };

  const handleSubmit = async () => {
    if (!formName.trim()) {
      setError('Name is required');
      return;
    }

    if (dialogState.mode === 'add' && !formFile) {
      setError('File is required');
      return;
    }

    setFormSubmitting(true);

    try {
      if (dialogState.mode === 'add') {
        const formData = new FormData();
        formData.append('name', formName);
        formData.append('description', formDescription);
        formData.append('displayOrder', formDisplayOrder.toString());
        if (formFile) {
          formData.append('file', formFile);
        }

        const response = await adminApiService.createResource(formData);
        if (response.success) {
          setSuccessMessage('Resource created successfully');
          handleCloseDialog();
          fetchResources();
        } else {
          setError('Failed to create resource');
        }
      } else if (dialogState.resource) {
        const response = await adminApiService.updateResource(dialogState.resource.id!, {
          name: formName,
          description: formDescription,
          displayOrder: formDisplayOrder,
        });
        if (response.success) {
          setSuccessMessage('Resource updated successfully');
          handleCloseDialog();
          fetchResources();
        } else {
          setError('Failed to update resource');
        }
      }
    } catch (err) {
      console.error('Error saving resource:', err);
      setError('An error occurred while saving the resource');
    } finally {
      setFormSubmitting(false);
    }
  };

  const handleDelete = async (resource: AdminResource) => {
    if (!window.confirm(`Are you sure you want to delete "${resource.name}"?`)) {
      return;
    }

    try {
      const response = await adminApiService.deleteResource(resource.id);
      if (response.success) {
        setSuccessMessage('Resource deleted successfully');
        fetchResources();
      } else {
        setError('Failed to delete resource');
      }
    } catch (err) {
      console.error('Error deleting resource:', err);
      setError('An error occurred while deleting the resource');
    }
  };

  const handleToggleActive = async (resource: AdminResource) => {
    try {
      const response = await adminApiService.updateResource(resource.id, {
        isActive: !resource.isActive,
      });
      if (response.success) {
        setSuccessMessage(
          `Resource ${resource.isActive ? 'deactivated' : 'activated'} successfully`
        );
        fetchResources();
      } else {
        setError('Failed to update resource status');
      }
    } catch (err) {
      console.error('Error updating resource:', err);
      setError('An error occurred while updating the resource');
    }
  };

  const handleDownload = async (resource: AdminResource) => {
    try {
      const response = await adminApiService.getResourceDownloadUrl(resource.id);
      if (response.success && response.downloadUrl) {
        const link = document.createElement('a');
        link.href = response.downloadUrl;
        link.download = resource.fileName;
        link.target = '_blank';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } else {
        setError('Failed to generate download link');
      }
    } catch (err) {
      console.error('Error downloading resource:', err);
      setError('An error occurred while downloading the file');
    }
  };

  const handleReplaceFile = async (resource: AdminResource, file: File) => {
    setReplacingFileId(resource.id);
    try {
      const response = await adminApiService.replaceResourceFile(resource.id, file);
      if (response.success) {
        setSuccessMessage('File replaced successfully');
        fetchResources();
      } else {
        setError('Failed to replace file');
      }
    } catch (err) {
      console.error('Error replacing file:', err);
      setError('An error occurred while replacing the file');
    } finally {
      setReplacingFileId(null);
    }
  };

  const columns: GridColDef[] = [
    {
      field: 'name',
      headerName: 'Name',
      flex: 1,
      minWidth: 150,
    },
    {
      field: 'description',
      headerName: 'Description',
      flex: 1.5,
      minWidth: 200,
      renderCell: (params: GridRenderCellParams) => (
        <Typography variant="body2" color="text.secondary" noWrap>
          {params.value || '-'}
        </Typography>
      ),
    },
    {
      field: 'fileName',
      headerName: 'File',
      flex: 1,
      minWidth: 150,
      renderCell: (params: GridRenderCellParams) => (
        <Tooltip title={params.value}>
          <Typography variant="body2" noWrap>
            {params.value}
          </Typography>
        </Tooltip>
      ),
    },
    {
      field: 'fileSize',
      headerName: 'Size',
      width: 100,
      renderCell: (params: GridRenderCellParams) => formatFileSize(params.value),
    },
    {
      field: 'displayOrder',
      headerName: 'Order',
      width: 80,
      align: 'center',
      headerAlign: 'center',
    },
    {
      field: 'isActive',
      headerName: 'Status',
      width: 100,
      renderCell: (params: GridRenderCellParams) => (
        <Chip
          label={params.value ? 'Active' : 'Inactive'}
          color={params.value ? 'success' : 'default'}
          size="small"
        />
      ),
    },
    {
      field: 'updatedAt',
      headerName: 'Updated',
      width: 160,
      renderCell: (params: GridRenderCellParams) => formatDate(params.value),
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: canManageResources ? 200 : 60,
      sortable: false,
      renderCell: (params: GridRenderCellParams<AdminResource>) => {
        const isReplacing = replacingFileId === params.row.id;
        return (
          <Box sx={{ display: 'flex', gap: 0.5 }}>
            <Tooltip title="Download">
              <span>
                <IconButton
                  size="small"
                  onClick={() => handleDownload(params.row)}
                  disabled={isReplacing}
                >
                  <DownloadIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
            {canManageResources && (
              <>
                <Tooltip title="Edit">
                  <span>
                    <IconButton
                      size="small"
                      onClick={() => handleOpenEditDialog(params.row)}
                      disabled={isReplacing}
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
                <Tooltip title={params.row.isActive ? 'Deactivate' : 'Activate'}>
                  <span>
                    <IconButton
                      size="small"
                      onClick={() => handleToggleActive(params.row)}
                      disabled={isReplacing}
                    >
                      {params.row.isActive ? (
                        <ToggleOnIcon fontSize="small" color="success" />
                      ) : (
                        <ToggleOffIcon fontSize="small" />
                      )}
                    </IconButton>
                  </span>
                </Tooltip>
                <Tooltip title={isReplacing ? 'Uploading...' : 'Replace File'}>
                  <span>
                    <IconButton
                      size="small"
                      component="label"
                      disabled={isReplacing}
                    >
                      {isReplacing ? (
                        <CircularProgress size={18} />
                      ) : (
                        <UploadIcon fontSize="small" />
                      )}
                      <input
                        type="file"
                        hidden
                        onChange={(e) => {
                          if (e.target.files?.[0]) {
                            handleReplaceFile(params.row, e.target.files[0]);
                          }
                        }}
                      />
                    </IconButton>
                  </span>
                </Tooltip>
                <Tooltip title="Delete">
                  <span>
                    <IconButton
                      size="small"
                      onClick={() => handleDelete(params.row)}
                      color="error"
                      disabled={isReplacing}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
              </>
            )}
          </Box>
        );
      },
    },
  ];

  const pageActions = canManageResources ? (
    <Button
      variant="contained"
      startIcon={<AddIcon />}
      onClick={handleOpenAddDialog}
    >
      Add Resource
    </Button>
  ) : undefined;

  return (
    <Layout pageTitle="Reporter Resources" pageActions={pageActions}>
      <Paper sx={{ height: 'calc(100vh - 200px)', width: '100%' }}>
        <DataGrid
          rows={resources}
          columns={columns}
          loading={loading}
          slots={{ toolbar: GridToolbar }}
          slotProps={{
            toolbar: {
              showQuickFilter: true,
              quickFilterProps: { debounceMs: 500 },
            },
          }}
          initialState={{
            sorting: {
              sortModel: [{ field: 'displayOrder', sort: 'asc' }],
            },
          }}
          pageSizeOptions={[10, 25, 50, 100]}
          disableRowSelectionOnClick
        />
      </Paper>

      {/* Add/Edit Dialog */}
      <Dialog
        open={dialogState.open}
        onClose={formSubmitting ? undefined : handleCloseDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {dialogState.mode === 'add' ? 'Add Resource' : 'Edit Resource'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              label="Name"
              value={formName}
              onChange={(e) => setFormName(e.target.value)}
              required
              fullWidth
              disabled={formSubmitting}
            />
            <TextField
              label="Description"
              value={formDescription}
              onChange={(e) => setFormDescription(e.target.value)}
              multiline
              rows={3}
              fullWidth
              disabled={formSubmitting}
            />
            <TextField
              label="Display Order"
              type="number"
              value={formDisplayOrder}
              onChange={(e) => setFormDisplayOrder(parseInt(e.target.value) || 0)}
              fullWidth
              helperText="Lower numbers appear first"
              disabled={formSubmitting}
            />
            {dialogState.mode === 'add' && (
              <Box>
                <Button
                  variant="outlined"
                  component="label"
                  startIcon={formSubmitting ? <CircularProgress size={16} /> : <UploadIcon />}
                  disabled={formSubmitting}
                >
                  {formSubmitting ? 'Uploading...' : 'Select File'}
                  <input
                    type="file"
                    hidden
                    disabled={formSubmitting}
                    onChange={(e) => {
                      if (e.target.files?.[0]) {
                        setFormFile(e.target.files[0]);
                      }
                    }}
                  />
                </Button>
                {formFile && (
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    Selected: {formFile.name} ({formatFileSize(formFile.size)})
                  </Typography>
                )}
              </Box>
            )}
            {dialogState.mode === 'edit' && dialogState.resource && (
              <Typography variant="body2" color="text.secondary">
                Current file: {dialogState.resource.fileName}
                <br />
                Use the "Replace File" button in the table to upload a new file.
              </Typography>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} disabled={formSubmitting}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={formSubmitting}
            startIcon={formSubmitting ? <CircularProgress size={16} /> : null}
          >
            {formSubmitting
              ? dialogState.mode === 'add'
                ? 'Uploading...'
                : 'Saving...'
              : dialogState.mode === 'add'
              ? 'Create'
              : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Error Snackbar */}
      <Snackbar
        open={!!error}
        autoHideDuration={6000}
        onClose={() => setError(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setError(null)} severity="error" sx={{ width: '100%' }}>
          {error}
        </Alert>
      </Snackbar>

      {/* Success Snackbar */}
      <Snackbar
        open={!!successMessage}
        autoHideDuration={4000}
        onClose={() => setSuccessMessage(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setSuccessMessage(null)} severity="success" sx={{ width: '100%' }}>
          {successMessage}
        </Alert>
      </Snackbar>
    </Layout>
  );
};

export default Resources;
